import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@comicrelief/component-library';
import ChevronIcon from '../assets/chevron-icon.svg';
import prependBaseUrl from '../utils/urlHelper';

import {
  SecondaryNavMenuWrapper,
  SecondaryNavLink,
  SecondaryNavLinkWithChildren,
  SecondaryChevronWrapper,
  ColumnWrapper,
  SecondaryNavItem,
  TertiaryNavMenu,
  TertiaryNavItem,
  TertiaryNavLink,
  NavCard,
  NavCardImage,
  NavCardContent,
  NavCardTitle,
  NavCardDescription,
  SecondaryMenuPrimaryLink,
  SecondaryMenuPrimaryLinkAnchor
} from './SecondaryNavMenu.style';

// Helper to group tertiary links (pageLevel: false)
// so that they sit under their parent secondary links (pageLevel: true)
const groupTertiaryLinks = (links) => {
  if (!links) return {};
  const groups = {};
  let currentSecondaryId = null;

  links.forEach((link) => {
    if (link.pageLevel) {
      currentSecondaryId = link.id;
      groups[currentSecondaryId] = [];
    } else if (currentSecondaryId) {
      groups[currentSecondaryId].push(link);
    }
  });

  return groups;
};

const SecondaryNavMenu = ({
  group,
  isSubMenuOpen,
  isTertiaryOpen,
  isNotDesktop,
  navHelperNew,
  internalLinkHelper,
  devMode,
  onTertiaryMenuChange,
  thisUrl,
  thisID,
  openedSubMenu
}) => {
  const [openTertiaryMenu, setOpenTertiaryMenu] = useState(null);

  const handleTertiaryToggle = (e, linkId) => {
    e.preventDefault();
    e.stopPropagation();
    const isOpening = openTertiaryMenu !== linkId;
    setOpenTertiaryMenu(isOpening ? linkId : null);

    if (onTertiaryMenuChange) {
      onTertiaryMenuChange(isOpening, isOpening ? group.primaryPageName : null, () => {
        setOpenTertiaryMenu(null);
        onTertiaryMenuChange(false, null, null);
      });
    }
  };

  // Reset tertiary menu state when the secondary submenu closes
  useEffect(() => {
    if (!openedSubMenu[thisID]) {
      setOpenTertiaryMenu(null);
    }
  }, [openedSubMenu, thisID]);

  // Collect all column links into a single array for iteration
  const allColumnLinks = useMemo(() => [
    group.column1PageLinks,
    group.column2PageLinks,
    group.column3PageLinks
  ], [group.column1PageLinks, group.column2PageLinks, group.column3PageLinks]);

  // Group tertiary links for all columns (run once per data change)
  const tertiaryGroups = useMemo(() => allColumnLinks.reduce(
    (acc, links) => ({ ...acc, ...groupTertiaryLinks(links) }), {}
  ), [allColumnLinks]);

  // All links flattened (run once per data change)
  const allLinks = useMemo(
    () => allColumnLinks.flatMap(links => links || []),
    [allColumnLinks]
  );

  // Check if a secondary link has tertiary children
  const hasTertiaryChildren = linkId => tertiaryGroups[linkId]?.length > 0;

  // Get the currently active tertiary menu data
  const activeTertiaryLinks = tertiaryGroups[openTertiaryMenu] || [];

  // Find the parent link (pageLevel: true) for the currently open tertiary menu
  const activeParentLink = useMemo(
    () => (openTertiaryMenu ? allLinks.find(link => link.id === openTertiaryMenu) : null),
    [openTertiaryMenu, allLinks]
  );

  // Pre-process URLs for all links (run once per data change)
  const processedLinkUrls = useMemo(() => {
    const urls = {};
    allLinks.forEach((link) => {
      urls[link.id] = internalLinkHelper(navHelperNew(link));
    });
    return urls;
  }, [allLinks, navHelperNew, internalLinkHelper]);

  // Helper to render a column's links
  const renderColumnLinks = (links) => {
    if (!links) return null;

    return links.map((child) => {
      // On mobile, only show secondary links (pageLevel: true)
      // Tertiary links (pageLevel: false) will be shown in the third modal
      if (isNotDesktop && !child.pageLevel) {
        return null;
      }

      const linkId = child.id;
      const hasChildren = hasTertiaryChildren(linkId);

      return (
        <SecondaryNavItem key={child.id} $isSecondary={child.pageLevel}>
          {hasChildren && isNotDesktop ? (
            <SecondaryNavLinkWithChildren
              href="#"
              inline
              role="menuitem"
              onClick={e => handleTertiaryToggle(e, linkId)}
              $isSecondary={child.pageLevel}
            >
              <Text>{child.pageName}</Text>
              <SecondaryChevronWrapper>
                <img src={ChevronIcon} alt="chevron icon" />
              </SecondaryChevronWrapper>
            </SecondaryNavLinkWithChildren>
          ) : (
            <SecondaryNavLink
              href={prependBaseUrl(processedLinkUrls[linkId], devMode)}
              inline
              role="menuitem"
              $isSecondary={child.pageLevel}
              {...(child.pageUrlIfExternal && { target: 'blank', rel: 'noreferrer' })}
            >
              <Text>{child.pageName}</Text>
            </SecondaryNavLink>
          )}
        </SecondaryNavItem>
      );
    });
  };

  return (
    <>
      {/* Second level of the navigation (ul tag): Child(ren) */}
      <SecondaryNavMenuWrapper
        isSubMenuOpen={isSubMenuOpen}
        isTertiaryOpen={isTertiaryOpen}
      >
        {/* Mobile: Show primary link at top of 2nd modal */}
        {isNotDesktop && (
          <SecondaryMenuPrimaryLink>
            <SecondaryMenuPrimaryLinkAnchor
              href={prependBaseUrl(thisUrl, devMode)}
              inline
              role="menuitem"
            >
              <Text>{group.primaryPageName}</Text>
            </SecondaryMenuPrimaryLinkAnchor>
          </SecondaryMenuPrimaryLink>
        )}

        {allColumnLinks.map((links, colIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <ColumnWrapper key={colIndex}>
            {/* Column 3: Cards if available (desktop only), otherwise links */}
            {colIndex === 2 && !isNotDesktop && group.column3PageCards?.length > 0 ? (
              group.column3PageCards.map((card) => {
                const isExternalCard = Boolean(card?.primaryPageUrlIfExternal);
                const thisCardUrl = card?.primaryPageUrlIfExternal || card?.pageSelector?.path;

                return (
                  <NavCard
                    key={card.id}
                    href={prependBaseUrl(thisCardUrl || '', devMode)}
                    {...(isExternalCard && { target: '_blank', rel: 'noreferrer' })}
                  >
                    {card.image?.url && (
                    <NavCardImage>
                      <img src={card.image.url} alt={card.image.title || card.pageName} />
                    </NavCardImage>
                    )}
                    <NavCardContent>
                      <NavCardTitle>{card.pageName}</NavCardTitle>
                      {card.pageDescription && (
                      <NavCardDescription>{card.pageDescription}</NavCardDescription>
                      )}
                    </NavCardContent>
                  </NavCard>
                );
              })
            ) : (
              renderColumnLinks(links)
            )}
          </ColumnWrapper>
        ))}
      </SecondaryNavMenuWrapper>

      {/* Third level modal - separate from secondary menu for independent visibility */}
      {isNotDesktop && (
        <TertiaryNavMenu isOpen={openTertiaryMenu !== null}>
          {/* Show parent link at the top */}
          {activeParentLink && (
            <TertiaryNavItem $isParent>
              <TertiaryNavLink
                href={prependBaseUrl(processedLinkUrls[activeParentLink.id], devMode)}
                inline
                role="menuitem"
                $isParent
                {...(activeParentLink.pageUrlIfExternal && { target: 'blank', rel: 'noreferrer' })}
              >
                <Text>{activeParentLink.pageName}</Text>
              </TertiaryNavLink>
            </TertiaryNavItem>
          )}
          {activeTertiaryLinks.map(tertiaryLink => (
            <TertiaryNavItem key={tertiaryLink.id}>
              <TertiaryNavLink
                href={prependBaseUrl(processedLinkUrls[tertiaryLink.id], devMode)}
                inline
                role="menuitem"
                {...(tertiaryLink.pageUrlIfExternal && { target: 'blank', rel: 'noreferrer' })}
              >
                <Text>{tertiaryLink.pageName}</Text>
              </TertiaryNavLink>
            </TertiaryNavItem>
          ))}
        </TertiaryNavMenu>
      )}
    </>
  );
};

SecondaryNavMenu.propTypes = {
  group: PropTypes.shape({
    primaryPageName: PropTypes.string.isRequired,
    column1PageLinks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      pageName: PropTypes.string
    })),
    column2PageLinks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      pageName: PropTypes.string
    })),
    column3PageLinks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      pageName: PropTypes.string
    })),
    column3PageCards: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      pageName: PropTypes.string,
      pageDescription: PropTypes.string,
      primaryPageUrlIfExternal: PropTypes.string,
      image: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        id: PropTypes.string
      })
    }))
  }).isRequired,
  isSubMenuOpen: PropTypes.bool.isRequired,
  isTertiaryOpen: PropTypes.bool.isRequired,
  isNotDesktop: PropTypes.bool.isRequired,
  navHelperNew: PropTypes.func.isRequired,
  internalLinkHelper: PropTypes.func.isRequired,
  devMode: PropTypes.bool,
  onTertiaryMenuChange: PropTypes.func,
  thisUrl: PropTypes.string.isRequired,
  thisID: PropTypes.string.isRequired,
  openedSubMenu: PropTypes.shape({}).isRequired
};

export default SecondaryNavMenu;
